import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Minus, Globe, BarChart2, QrCode, ArrowRight, ChevronRight, Lock } from 'lucide-react';
import Logo from '../components/Logo';

const HERO_IMG = 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=80';
const FOOD1 = 'https://images.unsplash.com/photo-1569050467447-ce54b3bbc37d?w=600&q=80';
const FOOD2 = 'https://images.unsplash.com/photo-1547592180-85f173990554?w=600&q=80';
const FOOD3 = 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=600&q=80';

function Landing() {
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      {/* NAV */}
      <nav style={styles.nav}>
        <Logo size={22} />
        <div style={styles.navBtns}>
          <button onClick={() => navigate('/login')} style={styles.btnOutline}>ログイン</button>
          <button onClick={() => navigate('/register')} style={styles.btnPrimary}>無料で始める</button>
        </div>
      </nav>

      {/* HERO */}
      <section style={styles.heroWrap}>
        <div style={styles.heroImgWrap}>
          <img src={HERO_IMG} alt="多言語QRメニューを導入した飲食店の店内" style={styles.heroImg} />
          <div style={styles.heroOverlay} />
        </div>
        <div style={styles.heroContent}>
          <p style={styles.heroEyebrow}>多言語メニュー × QRコード</p>
          <h1 style={styles.heroTitle}>
            日本語で入れるだけ。<br />外国人に伝わる<br />メニューができる。
          </h1>
          <p style={styles.heroSub}>
            自動翻訳で英語・中国語・韓国語に対応。<br />
            QRコードを置くだけで完成。
          </p>
          <div style={styles.heroCtas}>
            <button onClick={() => navigate('/register')} style={styles.btnHero}>
              無料で始める <ArrowRight size={16} style={{verticalAlign:'middle',marginLeft:6}} />
            </button>
            <p style={styles.heroNote}>クレジットカード不要</p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section style={styles.featuresSection}>
        <div style={styles.featuresInner}>
          <p style={styles.labelText}>FEATURES</p>
          <h2 style={styles.featuresTitle}>必要なものが、全部そろっている</h2>
          <div style={styles.featuresBig}>
            <div style={styles.featureBigCard}>
              <img src={FOOD1} alt="日本語入力だけで5言語に自動翻訳された多言語メニュー" style={styles.featureBigImg} />
              <div style={styles.featureBigBody}>
                <div style={styles.featureIconRow}><Globe size={20} color="#2d6a4f" strokeWidth={1.8} /></div>
                <h3 style={styles.featureBigTitle}>自動翻訳</h3>
                <p style={styles.featureBigText}>日本語で入力するだけ。英語・中国語・韓国語など5言語に自動翻訳。翻訳の手間はゼロ。</p>
              </div>
            </div>
            <div style={styles.featuresSmallCol}>
              <div style={styles.featureSmallCard}>
                <img src={FOOD2} alt="テーブルに設置したQRコードでメニューを読み取る様子" style={styles.featureSmallImg} />
                <div style={styles.featureSmallBody}>
                  <div style={styles.featureIconRow}><QrCode size={18} color="#2d6a4f" strokeWidth={1.8} /></div>
                  <h3 style={styles.featureSmallTitle}>QRコード</h3>
                  <p style={styles.featureSmallText}>テーブルに置くだけ。お客様のスマホで読み取れる。</p>
                </div>
              </div>
              <div style={styles.featureSmallCard}>
                <img src={FOOD3} alt="飲食店の売上・販売数を管理するダッシュボード画面" style={styles.featureSmallImg} />
                <div style={styles.featureSmallBody}>
                  <div style={styles.featureIconRow}><BarChart2 size={18} color="#2d6a4f" strokeWidth={1.8} /></div>
                  <h3 style={styles.featureSmallTitle}>売上管理</h3>
                  <p style={styles.featureSmallText}>1タップで販売数を記録。何が売れたか一目でわかる。</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section style={styles.pricingSection}>
        <div style={styles.pricingInner}>
          <p style={styles.labelText}>PRICING</p>
          <h2 style={styles.pricingTitle}>シンプルな料金プラン</h2>
          <p style={styles.pricingSub}>クレジットカード不要。いつでもキャンセル可能。</p>

          <div style={styles.plans}>
            {/* FREE */}
            <div style={styles.planCard}>
              <div style={styles.planTop}>
                <p style={styles.planName}>Free</p>
                <p style={styles.planPrice}>¥0<span style={styles.planPer}>/月</span></p>
                <p style={styles.planDesc}>まずは試してみたい方へ</p>
              </div>
              <div style={styles.planDivider} />
              <ul style={styles.planFeatures}>
                <li style={styles.featureItem}><Check size={14} color="#2d6a4f" strokeWidth={2.5} /><span>メニュー <strong>10品</strong>まで</span></li>
                <li style={styles.featureItem}><Check size={14} color="#2d6a4f" strokeWidth={2.5} /><span>日本語・<strong>英語</strong>の2言語</span></li>
                <li style={styles.featureItem}><Check size={14} color="#2d6a4f" strokeWidth={2.5} /><span>QRコード（テーブル無制限）</span></li>
                <li style={styles.featureItem}><Check size={14} color="#2d6a4f" strokeWidth={2.5} /><span>呼び出しボタン</span></li>
                <li style={{...styles.featureItem, ...styles.featureOff}}>
                  <Lock size={13} strokeWidth={2} />
                  <span>中国語・韓国語など <strong style={{color:'#f4a261'}}>Proのみ</strong></span>
                </li>
                <li style={{...styles.featureItem, ...styles.featureOff}}>
                  <Lock size={13} strokeWidth={2} />
                  <span>スタッフ注文管理 <strong style={{color:'#f4a261'}}>Proのみ</strong></span>
                </li>
                <li style={{...styles.featureItem, ...styles.featureOff}}>
                  <Lock size={13} strokeWidth={2} />
                  <span>在庫カウンター <strong style={{color:'#f4a261'}}>Proのみ</strong></span>
                </li>
                <li style={{...styles.featureItem, ...styles.featureOff}}>
                  <Lock size={13} strokeWidth={2} />
                  <span>週次売上分析 <strong style={{color:'#f4a261'}}>Proのみ</strong></span>
                </li>
              </ul>
              <button onClick={() => navigate('/register')} style={styles.btnPlanFree}>無料で始める</button>
            </div>

            {/* PRO */}
            <div style={{...styles.planCard, ...styles.planCardPro}}>
              <div style={styles.planTop}>
                <div style={styles.planBadgeRow}>
                  <p style={{...styles.planName, color:'#fff'}}>Pro</p>
                  <span style={styles.planBadge}>おすすめ</span>
                </div>
                <p style={{...styles.planPrice, color:'#fff'}}>¥980<span style={{...styles.planPer, color:'rgba(255,255,255,0.55)'}}>/月</span></p>
                <p style={{...styles.planDesc, color:'rgba(255,255,255,0.6)'}}>本格的に使いたい飲食店へ</p>
              </div>
              <div style={{...styles.planDivider, background:'rgba(255,255,255,0.12)'}} />
              <ul style={{...styles.planFeatures, color:'rgba(255,255,255,0.9)'}}>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>メニュー<strong style={{color:'#fff'}}>無制限</strong></span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>日・英・中・韓・他 <strong style={{color:'#fff'}}>5言語</strong></span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>QRコード（テーブル無制限）</span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>呼び出しボタン</span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>スタッフ注文管理</span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>在庫カウンター</span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>週次売上分析</span></li>
                <li style={styles.featureItem}><Check size={14} color="rgba(255,255,255,0.8)" strokeWidth={2.5} /><span>販売数管理</span></li>
              </ul>
              <button onClick={() => navigate('/register')} style={styles.btnPlanPro}>今すぐ始める</button>
            </div>
          </div>

          {/* 比較メモ */}
          <p style={styles.pricingNote}>
            ※ 無料プランはメニュー10品・英語のみ。中国語・韓国語対応や売上管理はProプランでご利用いただけます。
          </p>
        </div>
      </section>

      {/* CTA BAND */}
      <section style={styles.ctaBand}>
        <h2 style={styles.ctaTitle}>まずは無料で試してみる</h2>
        <p style={styles.ctaSub}>クレジットカード不要。10品まで永久無料。</p>
        <button onClick={() => navigate('/register')} style={styles.btnCtaBand}>
          無料アカウントを作る <ChevronRight size={16} style={{verticalAlign:'middle',marginLeft:4}} />
        </button>
      </section>

      <footer style={styles.footer}>
        <p style={styles.footerText}>© 2025 MenuSore. All rights reserved.</p>
      </footer>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Hiragino Sans", sans-serif',
    color: '#0f1923', background: '#fff',
  },
  nav: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 24px', borderBottom: '1px solid #f0f0f0',
    background: '#fff', position: 'sticky', top: 0, zIndex: 100,
  },
  navBtns: { display: 'flex', gap: '8px' },
  btnOutline: {
    padding: '7px 12px', border: '1.5px solid #2d6a4f',
    background: 'transparent', color: '#2d6a4f', borderRadius: '8px',
    cursor: 'pointer', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
  },
  btnPrimary: {
    padding: '7px 12px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '8px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
  },
  heroWrap: { position: 'relative', height: 'clamp(480px, 80vh, 680px)', overflow: 'hidden' },
  heroImgWrap: { position: 'absolute', inset: 0 },
  heroImg: { width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' },
  heroOverlay: {
    position: 'absolute', inset: 0,
    background: 'linear-gradient(to right, rgba(10,18,12,0.72) 0%, rgba(10,18,12,0.3) 100%)',
  },
  heroContent: {
    position: 'relative', zIndex: 2,
    display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    height: '100%', padding: 'clamp(24px,5vw,64px) clamp(24px,6vw,72px)',
    maxWidth: '720px',
  },
  heroEyebrow: {
    fontSize: '12px', fontWeight: '600', letterSpacing: '0.1em',
    color: 'rgba(255,255,255,0.7)', margin: '0 0 16px', textTransform: 'uppercase',
  },
  heroTitle: {
    fontSize: 'clamp(28px, 5.5vw, 52px)', fontWeight: '700', color: '#fff',
    lineHeight: '1.2', letterSpacing: '-0.02em', margin: '0 0 20px',
  },
  heroSub: {
    fontSize: 'clamp(14px, 2vw, 17px)', color: 'rgba(255,255,255,0.75)',
    lineHeight: '1.8', margin: '0 0 32px',
  },
  heroCtas: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px' },
  btnHero: {
    padding: '14px 28px', background: '#fff', color: '#0f1923',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '15px', fontWeight: '700', display: 'inline-flex', alignItems: 'center',
  },
  heroNote: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', margin: 0 },
  featuresSection: { padding: 'clamp(48px,8vw,96px) 24px', background: '#fff' },
  featuresInner: { maxWidth: '1000px', margin: '0 auto' },
  labelText: {
    fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em',
    color: '#2d6a4f', textTransform: 'uppercase', margin: '0 0 12px',
  },
  featuresTitle: {
    fontSize: 'clamp(22px, 4vw, 32px)', fontWeight: '700',
    letterSpacing: '-0.02em', margin: '0 0 40px', color: '#0f1923',
  },
  featuresBig: { display: 'flex', gap: '16px', alignItems: 'stretch', flexWrap: 'wrap' },
  featureBigCard: {
    flex: '1 1 320px', borderRadius: '16px', overflow: 'hidden',
    background: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column',
  },
  featureBigImg: { width: '100%', height: '220px', objectFit: 'cover' },
  featureBigBody: { padding: '24px' },
  featureIconRow: {
    width: '40px', height: '40px', borderRadius: '10px', background: '#e8f5e9',
    display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '14px',
  },
  featureBigTitle: { fontSize: '18px', fontWeight: '700', margin: '0 0 8px' },
  featureBigText: { fontSize: '14px', color: '#6b7280', lineHeight: '1.7', margin: 0 },
  featuresSmallCol: { flex: '1 1 240px', display: 'flex', flexDirection: 'column', gap: '16px' },
  featureSmallCard: {
    flex: 1, borderRadius: '16px', overflow: 'hidden',
    background: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column',
  },
  featureSmallImg: { width: '100%', height: '130px', objectFit: 'cover' },
  featureSmallBody: { padding: '18px' },
  featureSmallTitle: { fontSize: '15px', fontWeight: '700', margin: '0 0 6px' },
  featureSmallText: { fontSize: '13px', color: '#6b7280', lineHeight: '1.65', margin: 0 },
  pricingSection: { padding: 'clamp(48px,8vw,96px) 24px', background: '#f9fafb' },
  pricingInner: { maxWidth: '680px', margin: '0 auto', textAlign: 'center' },
  pricingTitle: {
    fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: '700',
    letterSpacing: '-0.02em', margin: '0 0 10px',
  },
  pricingSub: { fontSize: '14px', color: '#9ca3af', margin: '0 0 40px' },
  plans: { display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch' },
  planCard: {
    flex: '1 1 240px', maxWidth: '285px',
    background: '#fff', borderRadius: '20px', padding: '28px 24px',
    border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', textAlign: 'left',
  },
  planCardPro: {
    background: 'linear-gradient(150deg, #2d6a4f 0%, #1b4332 100%)',
    border: 'none', boxShadow: '0 12px 40px rgba(45,106,79,0.3)',
  },
  planTop: { marginBottom: '16px' },
  planBadgeRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' },
  planBadge: {
    background: '#f4a261', color: '#fff', fontSize: '10px', fontWeight: '700',
    padding: '2px 8px', borderRadius: '99px', letterSpacing: '0.05em',
  },
  planName: { fontSize: '16px', fontWeight: '700', color: '#0f1923', margin: '0 0 4px' },
  planPrice: {
    fontSize: '36px', fontWeight: '700', color: '#0f1923',
    letterSpacing: '-0.03em', margin: '8px 0 6px',
  },
  planPer: { fontSize: '14px', fontWeight: '400', color: '#9ca3af' },
  planDesc: { fontSize: '12px', color: '#9ca3af', margin: 0 },
  planDivider: { height: '1px', background: '#f0f0f0', margin: '16px 0' },
  planFeatures: {
    listStyle: 'none', padding: 0, margin: '0 0 24px',
    fontSize: '13px', color: '#374151',
    display: 'flex', flexDirection: 'column', gap: '10px', flex: 1,
  },
  featureItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  featureOff: { color: '#bbb' },
  btnPlanFree: {
    padding: '12px', background: '#f3f4f6', color: '#374151',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
  },
  btnPlanPro: {
    padding: '12px', background: '#f4a261', color: '#fff',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
  },
  pricingNote: {
    fontSize: '12px', color: '#9ca3af', margin: '24px auto 0',
    maxWidth: '500px', lineHeight: '1.7',
  },
  ctaBand: { padding: 'clamp(48px,8vw,80px) 24px', background: '#0f1923', textAlign: 'center' },
  ctaTitle: {
    fontSize: 'clamp(20px, 4vw, 28px)', fontWeight: '700',
    color: '#fff', margin: '0 0 10px', letterSpacing: '-0.01em',
  },
  ctaSub: { fontSize: '14px', color: 'rgba(255,255,255,0.5)', margin: '0 0 28px' },
  btnCtaBand: {
    padding: '14px 28px', background: '#2d6a4f', color: '#fff',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    fontSize: '15px', fontWeight: '600', display: 'inline-flex', alignItems: 'center',
  },
  footer: { textAlign: 'center', padding: '24px', borderTop: '1px solid #f0f0f0' },
  footerText: { color: '#d1d5db', fontSize: '12px', margin: 0 },
};

export default Landing;
